import { useState, useMemo, useRef } from 'react';
import { useReveal } from '@/lib/useReveal';
import useDocumentMeta from '@/lib/useDocumentMeta';
import useSiteSettings from '@/lib/useSiteSettings';
import { postEnquiry } from '@/lib/api';
import PictureImg from '@/components/PictureImg';

const OPTIONS = [
    {
        key: 'in_person',
        overline: 'I',
        title: 'In-Person Viewing',
        description:
            'Visit Maison Touré by appointment for a private presentation of the collection. Each viewing is tailored to your interests and offers time to experience the artworks in a calm and personal setting.',
        cta: 'Request an appointment',
    },
    {
        key: 'virtual',
        overline: 'II',
        title: 'Virtual Viewing',
        description:
            'Schedule a private virtual presentation with the founder and curator. The viewing is conducted live and allows you to explore selected works, discuss artists, ask questions and receive detailed guidance before making an acquisition.',
        cta: 'Schedule a virtual viewing',
    },
    {
        key: 'catalogue',
        overline: 'III',
        title: 'Tailored Digital Catalogue',
        description:
            'Receive a carefully curated digital catalogue based on your collecting interests. Each catalogue is individually prepared and may include selected artworks, artist biographies, installation views, provenance information and condition reports where appropriate.',
        cta: 'Request a catalogue',
    },
];

const BUDGETS = [
    'Under AUD 5,000',
    'AUD 5,000–10,000',
    'AUD 10,000–20,000',
    'AUD 20,000+',
    'Prefer not to say',
];

const initialForm = {
    viewing_type: 'in_person',
    name: '',
    email: '',
    country: '',
    phone: '',
    preferred_artists: '',
    artworks_of_interest: '',
    collecting_interests: '',
    budget_range: '',
    preferred_date: '',
    preferred_time: '',
    timezone: '',
    message: '',
};

export default function PrivateViewing() {
    useDocumentMeta({
        title: 'Private Viewing · Maison Touré',
        description:
            'A discreet collector service — in-person or virtual private viewings, or a tailored digital catalogue prepared for your interests.',
    });

    const reveal = useReveal();
    const settings = useSiteSettings();
    const pv = settings?.private_viewing || {};
    const ct = settings?.contact || {};

    const [form, setForm] = useState(initialForm);
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState('');
    const formRef = useRef(null);

    const update = (patch) => setForm((f) => ({ ...f, ...patch }));

    const selectOption = (key) => {
        update({ viewing_type: key });
        // Move focus to the form so keyboard users land on the request.
        setTimeout(() => {
            formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            formRef.current?.querySelector('input,select,textarea')?.focus?.({ preventScroll: true });
        }, 60);
    };

    const submit = async (e) => {
        e.preventDefault();
        setError('');
        setBusy(true);
        try {
            const payload = {
                ...form,
                type: 'private_viewing',
                message:
                    form.message ||
                    `${OPTIONS.find((o) => o.key === form.viewing_type)?.title || 'Private viewing'} request.`,
            };
            await postEnquiry(payload);
            setSent(true);
        } catch (err) {
            setError('Something went wrong. Please try again in a moment, or write to us directly.');
        } finally {
            setBusy(false);
        }
    };

    const selected = form.viewing_type;
    const heading = useMemo(
        () =>
            selected === 'in_person'
                ? 'In-Person Viewing'
                : selected === 'virtual'
                    ? 'Virtual Viewing'
                    : 'Tailored Digital Catalogue',
        [selected]
    );

    return (
        <div ref={reveal} data-testid="private-viewing-page">
            {/* ------------------------------------------------------------ */}
            {/* Hero (unchanged)                                             */}
            {/* ------------------------------------------------------------ */}
            <section className="relative h-[80vh] overflow-hidden">
                <div className="absolute inset-0 hero-fade">
                    {pv.hero_image && (
                        <PictureImg
                            src={pv.hero_image}
                            alt="Maison Touré gallery"
                            className="w-full h-full object-cover"
                            style={{ filter: 'brightness(0.86) contrast(1.03)' }}
                        />
                    )}
                </div>
                <div className="relative z-10 h-full flex items-end">
                    <div className="max-w-[1600px] mx-auto px-8 md:px-16 lg:px-20 pb-20 w-full">
                        <span className="overline" style={{ color: 'var(--bg)' }}>
                            {pv.hero_overline || 'By Appointment'}
                        </span>
                        <h1
                            className="mt-6"
                            style={{
                                color: 'var(--bg)',
                                fontFamily: "'Cormorant Garamond', Georgia, serif",
                                fontWeight: 400,
                                fontSize: 'clamp(46px, 6.6vw, 110px)',
                                lineHeight: 0.96,
                                letterSpacing: '-0.025em',
                                textShadow: '0 1px 24px rgba(20,18,15,0.32)',
                            }}
                        >
                            {pv.hero_headline || 'Private'}{' '}
                            <em className="serif-italic" style={{ fontWeight: 500 }}>
                                {pv.hero_headline_italic || 'viewing.'}
                            </em>
                        </h1>
                    </div>
                </div>
            </section>

            {/* ------------------------------------------------------------ */}
            {/* Intro — an invitation                                        */}
            {/* ------------------------------------------------------------ */}
            <section className="max-w-[1600px] mx-auto px-8 md:px-16 lg:px-20 py-32 md:py-48 grid grid-cols-1 md:grid-cols-12 gap-12">
                <div className="md:col-span-4 reveal">
                    <span className="overline" style={{ color: 'var(--ink-2)' }}>
                        An Invitation
                    </span>
                    <h2
                        className="mt-6"
                        style={{
                            fontFamily: "'Cormorant Garamond', Georgia, serif",
                            fontWeight: 400,
                            fontSize: 'clamp(32px, 3.4vw, 52px)',
                            lineHeight: 1.08,
                            letterSpacing: '-0.018em',
                        }}
                    >
                        Experience the collection in the way that{' '}
                        <em className="serif-italic" style={{ fontWeight: 500 }}>
                            suits you best.
                        </em>
                    </h2>
                </div>
                <div className="md:col-span-7 md:col-start-6 reveal">
                    <p
                        style={{
                            fontFamily: "'Cormorant Garamond', serif",
                            fontSize: 19,
                            lineHeight: 1.8,
                            color: 'var(--ink-2)',
                        }}
                    >
                        Maison Touré offers personalised ways to discover the collection, whether in Sydney
                        or from anywhere in the world. Each request is handled individually, with attention
                        to the collector&rsquo;s interests, preferred artists and level of engagement.
                    </p>
                </div>
            </section>

            {/* ------------------------------------------------------------ */}
            {/* Three options                                                */}
            {/* ------------------------------------------------------------ */}
            <section
                className="rule-top rule-bottom"
                data-testid="private-viewing-options"
                aria-label="Ways to experience the collection"
            >
                <div className="max-w-[1600px] mx-auto px-8 md:px-16 lg:px-20 py-24 md:py-32">
                    <div className="grid grid-cols-1 md:grid-cols-3">
                        {OPTIONS.map((opt, i) => {
                            const active = selected === opt.key;
                            return (
                                <article
                                    key={opt.key}
                                    className="reveal py-8 md:py-4 md:px-12 lg:px-16 flex flex-col"
                                    style={{
                                        borderLeft:
                                            i > 0 ? '1px solid var(--rule)' : 'none',
                                        borderTop:
                                            i > 0 ? '1px solid var(--rule)' : 'none',
                                    }}
                                    data-testid={`pv-option-${opt.key}`}
                                >
                                    <span className="overline" style={{ color: 'var(--ink-3)' }}>
                                        {opt.overline}
                                    </span>
                                    <h3
                                        className="mt-6"
                                        style={{
                                            fontFamily: "'Cormorant Garamond', Georgia, serif",
                                            fontWeight: 400,
                                            fontSize: 'clamp(26px, 2.4vw, 34px)',
                                            lineHeight: 1.15,
                                            letterSpacing: '-0.012em',
                                        }}
                                    >
                                        {opt.title}
                                    </h3>
                                    <p
                                        className="mt-6"
                                        style={{
                                            fontFamily: "'Cormorant Garamond', serif",
                                            fontSize: 17,
                                            lineHeight: 1.72,
                                            color: 'var(--ink-2)',
                                        }}
                                    >
                                        {opt.description}
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => selectOption(opt.key)}
                                        data-testid={`pv-option-cta-${opt.key}`}
                                        aria-pressed={active}
                                        className="mt-8 self-start text-left transition-opacity duration-300 hover:opacity-60 focus:outline-none focus:opacity-60"
                                        style={{
                                            fontFamily: "'Jost', sans-serif",
                                            fontSize: 11,
                                            letterSpacing: '0.24em',
                                            textTransform: 'uppercase',
                                            color: 'var(--ink)',
                                            paddingBottom: 3,
                                            borderBottom: `1px solid ${active ? 'var(--ink)' : 'var(--rule)'}`,
                                        }}
                                    >
                                        {opt.cta}
                                        <span aria-hidden="true" style={{ marginLeft: 10 }}>→</span>
                                    </button>
                                </article>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ------------------------------------------------------------ */}
            {/* Collector request form                                       */}
            {/* ------------------------------------------------------------ */}
            <section className="rule-top" data-testid="private-access-form" ref={formRef}>
                <div className="max-w-[1600px] mx-auto px-8 md:px-16 lg:px-20 py-32 md:py-48 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20">
                    <div className="md:col-span-5 reveal">
                        <span className="overline" style={{ color: 'var(--ink-2)' }}>
                            Request
                        </span>
                        <h2
                            className="mt-6"
                            style={{
                                fontFamily: "'Cormorant Garamond', serif",
                                fontWeight: 400,
                                fontSize: 'clamp(36px, 4.4vw, 64px)',
                                lineHeight: 1.04,
                                letterSpacing: '-0.02em',
                            }}
                        >
                            {heading.split(' ').slice(0, -1).join(' ') || 'Begin a'}{' '}
                            <em className="serif-italic" style={{ fontWeight: 500 }}>
                                {heading.split(' ').slice(-1)[0]}.
                            </em>
                        </h2>
                        <p
                            className="mt-10"
                            style={{
                                fontFamily: "'Cormorant Garamond', serif",
                                fontSize: 18,
                                lineHeight: 1.75,
                                color: 'var(--ink-2)',
                            }}
                        >
                            Share a little about what you are drawn to. Each request is answered personally,
                            usually within one business day.
                        </p>
                        <div
                            className="mt-12 overline"
                            style={{ color: 'var(--ink-3)', lineHeight: 1.9 }}
                        >
                            Sydney &nbsp;·&nbsp; By appointment
                            <br />
                            <span
                                style={{
                                    textTransform: 'none',
                                    letterSpacing: 0,
                                    fontFamily: "'Cormorant Garamond', serif",
                                    fontStyle: 'italic',
                                    fontSize: 15,
                                }}
                            >
                                {ct.phone && (
                                    <>
                                        <a href={`tel:${(ct.phone || '').replace(/\s+/g, '')}`}>{ct.phone}</a>
                                        &nbsp;·&nbsp;
                                    </>
                                )}
                                {ct.email_primary && (
                                    <a href={`mailto:${ct.email_primary}`}>{ct.email_primary}</a>
                                )}
                            </span>
                        </div>
                    </div>

                    <div className="md:col-span-6 md:col-start-7 reveal">
                        {sent ? (
                            <div data-testid="pv-success" className="py-12">
                                <span className="overline" style={{ color: 'var(--ink-2)' }}>
                                    Received
                                </span>
                                <h3
                                    className="mt-6"
                                    style={{
                                        fontFamily: "'Cormorant Garamond', serif",
                                        fontWeight: 400,
                                        fontSize: 'clamp(36px, 4vw, 56px)',
                                        lineHeight: 1.08,
                                        letterSpacing: '-0.02em',
                                    }}
                                >
                                    Thank you. Your request has been{' '}
                                    <em className="serif-italic" style={{ fontWeight: 500 }}>
                                        received.
                                    </em>
                                </h3>
                                <p
                                    className="mt-8"
                                    style={{
                                        fontFamily: "'Cormorant Garamond', serif",
                                        fontSize: 18,
                                        lineHeight: 1.75,
                                        color: 'var(--ink-2)',
                                    }}
                                >
                                    Maison Touré will respond shortly with a personalised follow-up.
                                </p>
                            </div>
                        ) : (
                            <form
                                onSubmit={submit}
                                className="flex flex-col gap-8"
                                data-testid="pv-form"
                                noValidate
                            >
                                {/* Viewing type ------------------------------------------- */}
                                <fieldset data-testid="pv-viewing-type" className="border-0 p-0 m-0">
                                    <legend className="overline mb-5" style={{ color: 'var(--ink-2)' }}>
                                        How would you like to experience the collection?
                                    </legend>
                                    <div className="flex flex-col gap-3">
                                        {OPTIONS.map((opt) => {
                                            const active = form.viewing_type === opt.key;
                                            return (
                                                <label
                                                    key={opt.key}
                                                    data-testid={`pv-radio-${opt.key}`}
                                                    className="flex items-center gap-4 cursor-pointer group"
                                                    style={{
                                                        paddingBlock: 10,
                                                        borderBottom: '1px solid var(--rule)',
                                                    }}
                                                >
                                                    <input
                                                        type="radio"
                                                        name="viewing_type"
                                                        value={opt.key}
                                                        checked={active}
                                                        onChange={() => update({ viewing_type: opt.key })}
                                                        className="sr-only"
                                                    />
                                                    <span
                                                        aria-hidden="true"
                                                        style={{
                                                            width: 10,
                                                            height: 10,
                                                            borderRadius: '50%',
                                                            border: '1px solid var(--ink)',
                                                            background: active ? 'var(--ink)' : 'transparent',
                                                            transition: 'background 220ms ease',
                                                            flexShrink: 0,
                                                        }}
                                                    />
                                                    <span
                                                        style={{
                                                            fontFamily: "'Cormorant Garamond', serif",
                                                            fontSize: 19,
                                                            color: active ? 'var(--ink)' : 'var(--ink-2)',
                                                            letterSpacing: '-0.005em',
                                                        }}
                                                    >
                                                        {opt.title}
                                                    </span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </fieldset>

                                {/* Identity -------------------------------------------------- */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                                    <div>
                                        <label htmlFor="pv-name-input" className="sr-only">Full name</label>
                                        <input
                                            id="pv-name-input"
                                            data-testid="pv-name"
                                            required
                                            placeholder="Full name*"
                                            value={form.name}
                                            onChange={(e) => update({ name: e.target.value })}
                                            className="input-line w-full"
                                            autoComplete="name"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="pv-email-input" className="sr-only">Email</label>
                                        <input
                                            id="pv-email-input"
                                            data-testid="pv-email"
                                            type="email"
                                            required
                                            placeholder="Email*"
                                            value={form.email}
                                            onChange={(e) => update({ email: e.target.value })}
                                            className="input-line w-full"
                                            autoComplete="email"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="pv-country-input" className="sr-only">Country</label>
                                        <input
                                            id="pv-country-input"
                                            data-testid="pv-country"
                                            required
                                            placeholder="Country*"
                                            value={form.country}
                                            onChange={(e) => update({ country: e.target.value })}
                                            className="input-line w-full"
                                            autoComplete="country-name"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="pv-phone-input" className="sr-only">Phone</label>
                                        <input
                                            id="pv-phone-input"
                                            data-testid="pv-phone"
                                            placeholder="Phone (optional)"
                                            value={form.phone}
                                            onChange={(e) => update({ phone: e.target.value })}
                                            className="input-line w-full"
                                            autoComplete="tel"
                                        />
                                    </div>
                                </div>

                                {/* Collecting preferences ---------------------------------- */}
                                <div className="flex flex-col gap-8">
                                    <div>
                                        <label htmlFor="pv-artists" className="sr-only">Preferred artists</label>
                                        <input
                                            id="pv-artists"
                                            data-testid="pv-preferred-artists"
                                            placeholder="Preferred artists"
                                            value={form.preferred_artists}
                                            onChange={(e) => update({ preferred_artists: e.target.value })}
                                            className="input-line w-full"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="pv-works" className="sr-only">Artworks of interest</label>
                                        <input
                                            id="pv-works"
                                            data-testid="pv-artworks-of-interest"
                                            placeholder="Artworks of interest"
                                            value={form.artworks_of_interest}
                                            onChange={(e) => update({ artworks_of_interest: e.target.value })}
                                            className="input-line w-full"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="pv-interests" className="sr-only">Collecting interests</label>
                                        <input
                                            id="pv-interests"
                                            data-testid="pv-collecting-interests"
                                            placeholder="Collecting interests (mediums, regions, movements…)"
                                            value={form.collecting_interests}
                                            onChange={(e) => update({ collecting_interests: e.target.value })}
                                            className="input-line w-full"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                                    <div>
                                        <label htmlFor="pv-budget" className="sr-only">Budget range</label>
                                        <select
                                            id="pv-budget"
                                            data-testid="pv-budget"
                                            value={form.budget_range}
                                            onChange={(e) => update({ budget_range: e.target.value })}
                                            className="input-line w-full"
                                            style={{ appearance: 'none' }}
                                        >
                                            <option value="">Budget range</option>
                                            {BUDGETS.map((b) => (
                                                <option key={b} value={b}>{b}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* In-person: date */}
                                    {selected === 'in_person' && (
                                        <div>
                                            <label htmlFor="pv-date" className="sr-only">Preferred date</label>
                                            <input
                                                id="pv-date"
                                                data-testid="pv-preferred-date"
                                                type="date"
                                                placeholder="Preferred date"
                                                value={form.preferred_date}
                                                onChange={(e) => update({ preferred_date: e.target.value })}
                                                className="input-line w-full"
                                            />
                                        </div>
                                    )}

                                    {/* Virtual: timezone */}
                                    {selected === 'virtual' && (
                                        <div>
                                            <label htmlFor="pv-tz" className="sr-only">Preferred timezone</label>
                                            <input
                                                id="pv-tz"
                                                data-testid="pv-timezone"
                                                placeholder="Preferred timezone (e.g. Sydney AEDT)"
                                                value={form.timezone}
                                                onChange={(e) => update({ timezone: e.target.value })}
                                                className="input-line w-full"
                                            />
                                        </div>
                                    )}
                                </div>

                                {/* In-person: preferred time. Virtual: preferred date. */}
                                {(selected === 'in_person' || selected === 'virtual') && (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                                        {selected === 'in_person' && (
                                            <div>
                                                <label htmlFor="pv-time" className="sr-only">Preferred time</label>
                                                <input
                                                    id="pv-time"
                                                    data-testid="pv-preferred-time"
                                                    placeholder="Preferred time (e.g. morning, 3pm)"
                                                    value={form.preferred_time}
                                                    onChange={(e) => update({ preferred_time: e.target.value })}
                                                    className="input-line w-full"
                                                />
                                            </div>
                                        )}
                                        {selected === 'virtual' && (
                                            <>
                                                <div>
                                                    <label htmlFor="pv-date2" className="sr-only">Preferred date</label>
                                                    <input
                                                        id="pv-date2"
                                                        data-testid="pv-preferred-date"
                                                        type="date"
                                                        value={form.preferred_date}
                                                        onChange={(e) => update({ preferred_date: e.target.value })}
                                                        className="input-line w-full"
                                                    />
                                                </div>
                                                <div>
                                                    <label htmlFor="pv-time2" className="sr-only">Preferred time</label>
                                                    <input
                                                        id="pv-time2"
                                                        data-testid="pv-preferred-time"
                                                        placeholder="Preferred time"
                                                        value={form.preferred_time}
                                                        onChange={(e) => update({ preferred_time: e.target.value })}
                                                        className="input-line w-full"
                                                    />
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}

                                <div>
                                    <label htmlFor="pv-message-input" className="sr-only">Message</label>
                                    <textarea
                                        id="pv-message-input"
                                        data-testid="pv-message"
                                        rows={5}
                                        placeholder={
                                            selected === 'catalogue'
                                                ? 'Anything else that would help us prepare your catalogue?'
                                                : 'A note (optional) — a moment in your collection, an artist you already follow…'
                                        }
                                        value={form.message}
                                        onChange={(e) => update({ message: e.target.value })}
                                        className="input-line w-full"
                                    />
                                </div>

                                {error && (
                                    <p
                                        role="alert"
                                        data-testid="pv-error"
                                        style={{
                                            fontFamily: "'Cormorant Garamond', serif",
                                            fontStyle: 'italic',
                                            fontSize: 15,
                                            color: '#8a2f1e',
                                        }}
                                    >
                                        {error}
                                    </p>
                                )}

                                <button
                                    type="submit"
                                    data-testid="pv-submit"
                                    disabled={busy}
                                    className="btn-primary self-start mt-4"
                                >
                                    {busy ? 'Sending' : 'Send request'}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
}
